import {AdvanceTermFilter, InFilter, OperatorFilter, TermFilter} from '@dofleini/query-builder';
import {useUser} from '@dofleini/security';
import {CubaCIRuler, NameRuler} from '@dofleini/validator';
import {activityPlaces, companies, nomenclators, procedures, proposalrequest, users} from '@mep/pae-commons';
import i18n from 'i18next';
import cloneDeep from 'lodash/cloneDeep';
import last from 'lodash/last';
import set from 'lodash/set';
import take from 'lodash/take';
import _uniq from 'lodash/uniq';
import moment from 'moment';
import React, {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import BooleanPreview from '@/components/BooleanPreview';
import DatePreview from '@/components/DatePreview';
import {DynamicFieldDatePicker} from '@/components/DynamicFormCreate/DynamicFormFields';
import Editor from '@/components/Editor';
import FormTitleSession from '@/components/FormTitleSession';
import InfoBox from '@/components/InfoBox';
import {renderMunicipalitySelect} from '@/components/MunicipalitySelect';
import PhoneEditor from '@/components/PhoneNumberField/PhoneEditor';
import ProvinceSelect from '@/components/ProvinceSelect';
import UploadFileDragger from '@/components/UploadFileDragger';
import {renderActivityPlaceLinkTypeSelect} from '@/modules/activityPlace/components/ActivityPlaceLinkTypeSelect';
import {useProcessSettings} from '@/modules/actors/contexts/ProcessSettingsContext';
import DissolutionWarning from '@/modules/company/components/DissolutionWarning';
import HelpDocSection from '@/modules/company/components/HelpDocSection';
import NotaryProcedureTypesSelect from '@/modules/company/components/NotaryProcedureTypesSelect';
import {renderMapViewer} from '@/modules/company/components/OfficeMap/components/MapViewer';
import {CACHE_KEY_LIST_PROCEDURE, CACHE_KEY_PROCEDURE} from '@/modules/company/contants/procedure';
import {useProcedure} from '@/modules/company/contexts/ProcedureContext';
import ProcedureApiService from '@/modules/company/services/ProcedureApiService';
import CauseSelect from '@/modules/configuration/components/CauseSelect';
import CNAESelect from '@/modules/configuration/components/CNAESelect';
import NotariesSelect from '@/modules/configuration/components/NotariesSelect';
import ProtectedAreaSelect from '@/modules/configuration/components/ProtectedAreaSelect';
import {CAUSE_TYPES} from '@/modules/configuration/contants/cause';
import {useCountProtectedAreasByProvince} from '@/modules/configuration/hooks/useProtectedArea';
import {useSettings} from '@/modules/configuration/hooks/useSettings';
import {renderAssignNotaryOffice} from '@/modules/proposalFiles/components/AssignNotaryOffice';
import ActivityPlacesSection from '@/modules/proposalRequest/components/ActivityPlacesSection';
import ModelProposalDownload from '@/modules/proposalRequest/components/ModelProposalDownload';
import ProposalActivitiesSelect from '@/modules/proposalRequest/components/ProposalActivitiesSelect';
import ProposalCapitalSummary from '@/modules/proposalRequest/components/ProposalCapitalSummary';
import ProposalPartnerImportSelect
  from '@/modules/proposalRequest/components/ProposalPartnerImportSelect/ProposalPartnerImportSelect';
import ProposalPartnersAdministration from '@/modules/proposalRequest/components/ProposalPartnersAdministration';
import ProposalPrivatePartners from '@/modules/proposalRequest/components/ProposalPrivatePartners';
import ProposalStatePartners from '@/modules/proposalRequest/components/ProposalStatePartners';
import ProposalTrademark from '@/modules/proposalRequest/components/ProposalTrademark';
import QuestionWithDetails, {renderQuestionWithDetails} from '@/modules/proposalRequest/components/QuestionWithDetails';
import {
  DIALOG_ADMIN_GENERAL_PARTNER_NAMESPACE,
  DIALOG_CONTROL_GENERAL_PARTNER_NAMESPACE,
  MODELS_FILE_KEYS
} from '@/modules/proposalRequest/contants/proposalRequest';
import {useProposalCreation} from '@/modules/proposalRequest/contexts/ProposalRequestCreation';
import {posterFields, representationFields} from '@/modules/proposalRequest/hooks/projectFields';
import {usePartnersCapital} from '@/modules/proposalRequest/hooks/usePartnersCapital';
import {getSocialDenominationSuffix} from '@/modules/proposalRequest/hooks/useSocialDenominationType';
import createClearRenderView from '@/utils/createClearRenderView';
import {createFieldsHook} from '@/utils/createFieldsHook';
import createFormFieldset from '@/utils/createFormFieldset';
import {createGetOneHook} from '@/utils/createGetOneHook';
import {validations} from '@/utils/fields';
import {renderProvince} from '@/utils/location';
import {getOrElse} from '@/utils/utils';

const {CNA, STATE_MIPYME, ORGANIZATION_PMS, TCP_PROPOSAL} = proposalrequest;
const {ACTIVITY_PLACE_LINK_TYPES} = activityPlaces;
const {USER_TYPES} = users;
const {COMPANY_STATUS} = companies;
const {PROCEDURE_TYPES} = procedures;
const {CAUSES_IDENTIFIERS} = nomenclators;

export const useGetOneProcedure = createGetOneHook(ProcedureApiService, 'id', CACHE_KEY_PROCEDURE);
export const useFields = createFieldsHook('procedure', []);

export const useProcedureGeneralFields = (isViewMode, form, columns, initialValues) => {
  const {t} = useTranslation('proposalRequest');
  return useMemo(() => {
    let fieldsMap = [];
    const isDirectUpdate = initialValues?.isDirectUpdate;
    const isTCP = TCP_PROPOSAL === initialValues?.subjectType;
    
    if (!isViewMode) {
      if (!isTCP)
        fieldsMap.splice(
          1,
          0,
          {
            key: 'extraInfo',
            colSpan: 3,
            render: createFormFieldset({
              title: 'procedure.notaryProcedure.help',
              translation: 'company',
              help: 'procedure.notaryProcedure.helpMore',
              content: <HelpDocSection/>
            }),
          },
          {
            key: 'partnersProcedureAval',
            label: t('proposalRequest.form.partnersProcedureAval'),
            required: true,
            colSpan: 4,
            widget: UploadFileDragger,
            initialValue: getOrElse(initialValues?.procedureValues, 'partnersProcedureAval', 'general.partnersProcedureAval'),
            widgetProps: {
              isImage: false,
              listType: 'picture',
              descriptionText: t('proposalRequest.form.upload'),
              maxCount: 10,
              multiple: true,
            },
          },
          {
            key: 'extraFiles',
            label: t('company:company.extraFiles'),
            required: false,
            colSpan: 4,
            widget: UploadFileDragger,
            widgetProps: {
              isImage: false,
              listType: 'picture',
              maxCount: 20,
              multiple: true
            },
            extra: <InfoBox
              title={'notarialProcess.documents.help'}
              translate={'proposalRequest'}
            />
          },
        );
      
      fieldsMap.splice(
        isTCP ? 1 : 3,
        0,
        {
          key: 'contactInfo',
          colSpan: 3,
          render: createFormFieldset({
            title: 'procedure.contactHelp',
            translation: 'company'
          }),
        },
        {
          key: 'phones',
          initialValue: getOrElse(initialValues?.procedureValues, 'phones', 'general.phones'),
          colSpan: 3,
          label: t('company:company.phoneNumbers'),
          rules: [validations.listPhones()],
          widget: PhoneEditor,
          widgetProps: {
            size: 'large',
          },
          required: true,
        },
        {
          key: 'description',
          initialValue: getOrElse(initialValues?.procedureValues, 'description', 'general.description'),
          colSpan: 3,
          label: t('common:description'),
          widget: 'textarea',
          widgetProps: {autoSize: {minRows: 5, maxRows: 12}},
          required: false,
          className: 'form-view-mode-vertical-item'
        },
      );
      
      if (!isDirectUpdate && !isTCP)
        fieldsMap.splice(2, 0,
          {
            key: 'notaryOffice',
            initialValue: getOrElse(initialValues?.procedureValues, 'notaryOffice', 'general.notaryOffice'),
            colSpan: 3,
            render: renderQuestionWithDetails,
            widgetProps: {
              label: 'notarialProcess.office.label',
              extraLabel: 'where',
              translate: 'proposalRequest',
              placeholderText: 'selectPlaceholder',
              size: 'large',
              customWhereCmp: NotariesSelect,
              filters: new OperatorFilter({
                type: 'AND',
                filters: [
                  new TermFilter({field: 'address.province', value: initialValues?.address?.province})
                ]
              }),
            }
          },
          {
            key: 'notary',
            initialValue: getOrElse(initialValues?.procedureValues, 'notary', 'general.notary'),
            colSpan: 3,
            label: t('notarialProcess.notary.label'),
            render: renderQuestionWithDetails,
            widgetProps: {
              translate: 'proposalRequest',
              extraLabel: 'notarialProcess.notary.name',
              size: 'large',
              cmpProps: {
                extra: <InfoBox
                  title={'notarialProcess.notary.help'}
                  translate={'proposalRequest'}
                />
              }
            }
          }
        );
    }
    
    return fieldsMap;
  }, [initialValues, isViewMode, t]);
};

export const useProcedureCompanyName = (isViewMode, form) => {
  const {user} = useUser();
  const {t} = useTranslation('proposalRequest');
  const {subjectType} = form?.getFieldsValue();
  const {data: settings} = useSettings();
  
  const type = useMemo(() => subjectType || user?.subjectType, [subjectType, user?.subjectType]);
  
  return useMemo(() => {
    let fieldsMap = [];
    
    if (!isViewMode) {
      fieldsMap.splice(
        1,
        0,
        {
          key: 'extraInfo',
          colSpan: 3,
          render: createFormFieldset({
            title: 'proposalRequest.form.general.groups.extra',
            translation: 'proposalRequest',
          }),
        },
        {
          key: 'socialDenomination',
          colSpan: 3,
          label: t('proposalRequest.form.general.name.label'),
          help: (
            <InfoBox
              title={'proposalRequest.form.general.name.help'}
              translate={'proposalRequest'}
            />
          ),
          placeholder: t('defaultPlaceholder'),
          hasFeedback: true,
          required: true,
          widgetProps: {
            suffix: getSocialDenominationSuffix(type, user?.isSoleOwner),
            size: 'large',
            // autoFocus: true,
            type: 'text',
          },
          rules: [validations.bannedWordsCheck(settings)],
        },
        {
          key: 'haveTradeMark',
          colSpan: 3,
          placeholder: 'defaultPlaceholder',
          required: false,
          widget: ProposalTrademark,
          widgetProps: {
            label: 'proposalRequest.form.general.haveTradeMark.label',
            size: 'large',
            translate: 'proposalRequest',
            type: 'text',
          },
          rules: [
            {
              validator: (rule, value) => {
                return new Promise((resolve, reject) => {
                  if (value?.active && !value?.file)
                    reject(new Error(i18n.t('validateMessages.required')));
                  else {
                    resolve();
                  }
                });
              },
            },
          ],
        }
      );
    }
    
    return fieldsMap;
  }, [isViewMode, t, type, user?.isSoleOwner, settings]);
};

export const useProcedureCorporatePurpose = (isViewMode, form) => {
  const {user} = useUser();
  const {t} = useTranslation('proposalRequest');
  const {activities, mainActivity, subjectType} = form?.getFieldsValue();
  
  const type = useMemo(() => subjectType || user?.subjectType, [subjectType, user?.subjectType]);
  
  useEffect(() => {
    const exist = activities?.some(e => (e?.id || e) === mainActivity);
    if (exist) {
      const values = form.getFieldsValue(true);
      set(values, 'activities', activities?.filter(e => (e?.id || e) !== mainActivity));
      form.setFieldsValue(cloneDeep(values));
    }
  }, [activities, form, mainActivity]);
  
  return useMemo(() => {
    const fieldsMap = [];
    const isStatePyme = [STATE_MIPYME, ORGANIZATION_PMS].includes(type);
    
    if (!isViewMode) {
      const activityDeclarationIndex = fieldsMap.findIndex((e) => e.key === 'activityDeclaration');
      
      fieldsMap.splice(
        activityDeclarationIndex + 1,
        0,
        {
          key: 'activityDeclaration',
          colSpan: 3,
          render: createFormFieldset({
            title: 'proposalRequest.form.general.groups.activities',
            help: 'proposalRequest.form.general.groups.activities_help',
            translation: 'proposalRequest',
          }),
        },
        {
          key: 'socialObject',
          label: t('proposalRequest.form.general.socialObject.label'),
          widget: Editor,
          colSpan: 3,
          widgetProps: {size: 'large'},
          required: true,
          className: 'form-view-mode-vertical-item',
        },
        {
          key: 'mainActivity',
          colSpan: 3,
          label: t('proposalRequest.form.general.mainActivity.label'),
          placeholder: t('selectPlaceholder'),
          required: true,
          dependencies: ['activities'],
          widget: CNAESelect,
          widgetProps: {
            size: 'large',
            filters: !isStatePyme
              ? new TermFilter({field: 'isProhibited', value: false}).toQuery()
              : null
          },
        },
        {
          key: 'otherActivities',
          label: t('proposalRequest.form.general.activities.textLabel'),
          colSpan: 3,
          widget: Editor,
          widgetProps: {size: 'large'},
          className: 'form-view-mode-vertical-item',
        },
        {
          key: 'activities',
          colSpan: 3,
          label: t('proposalRequest.form.general.activities.label'),
          placeholder: t('selectPlaceholder'),
          required: false,
          dependencies: ['mainActivity'],
          widget: ProposalActivitiesSelect,
          extra: (
            <InfoBox
              title={'proposalRequest.form.general.activities.help'}
              translate={'proposalRequest'}
            />
          ),
          widgetProps: {
            filters: !isStatePyme
              ? new TermFilter({field: 'isProhibited', value: false}).toQuery()
              : null,
            mainActivity: mainActivity,
            size: 'large',
          },
        },
        {
          key: 'projectDescription',
          label: t('proposalRequest.form.general.projectDescription.label'),
          widget: Editor,
          colSpan: 3,
          widgetProps: {size: 'large'},
          required: true,
          extra: <InfoBox title={'proposalRequest.form.general.projectDescription.help'}
            translate={'proposalRequest'}/>,
          className: 'form-view-mode-vertical-item'
        },
      );
    }
    return fieldsMap;
  }, [type, isViewMode, t, mainActivity]);
};

export const useProcedureOtherActivities = (isViewMode, form) => {
  const {t} = useTranslation('proposalRequest');
  const {mainActivity, activities} = form?.getFieldsValue();
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      const activityDeclarationIndex = fieldsMap.findIndex((e) => e.key === 'activityDeclaration');
      
      fieldsMap.splice(
        activityDeclarationIndex + 1,
        0,
        {
          key: 'activities',
          colSpan: 3,
          label: t('proposalRequest.form.general.activities.label'),
          placeholder: t('selectPlaceholder'),
          required: false,
          dependencies: ['mainActivity'],
          widget: ProposalActivitiesSelect,
          extra: (
            <InfoBox
              title={'proposalRequest.form.general.activities.help2'}
              translate={'proposalRequest'}
            />
          ),
          widgetProps: {
            filters: new AdvanceTermFilter({type: 'NIN', field: '_id', value: activities?.map(e => e.id)}).toQuery(),
            mainActivity: mainActivity,
            size: 'large',
          },
        }
      );
    }
    return fieldsMap;
  }, [activities, isViewMode, mainActivity, t]);
};

export const useProcedureRegisteredOffice = (isViewMode, form) => {
  const {user} = useUser();
  const {t} = useTranslation('proposalRequest');
  const {activities, mainActivity, address: location, subjectType, linkType} = form?.getFieldsValue();
  const {data, isLoading} = useCountProtectedAreasByProvince(location);
  const {data: settings} = useSettings();
  
  const type = useMemo(() => subjectType || user?.subjectType, [subjectType, user?.subjectType]);
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      fieldsMap.splice(
        3,
        0,
        {
          key: 'socialAddress',
          colSpan: 3,
          render: createFormFieldset({
            title: 'proposalRequest.form.general.socialAddress.label',
            help: 'proposalRequest.form.general.socialAddress.help',
            translation: 'proposalRequest',
          }),
        },
        {
          key: 'address.province',
          required: true,
          colSpan: 1,
          label: t('province'),
          widget: ProvinceSelect,
          initialValue: user?.address && user?.address.province,
          widgetProps: {
            size: 'large',
            onChange: () => form.setFieldsValue({bankType: '', bankOffice: ''}),
          },
          viewWidget: ({value}) => renderProvince(value),
        },
        {
          key: 'address.municipality',
          label: t('municipality'),
          required: true,
          onlyEdit: true,
          initialValue: user?.address && user?.address.municipality,
          colSpan: 1,
          widgetProps: {
            size: 'large',
          },
          render: ({...rest}) => {
            return renderMunicipalitySelect(rest, location?.province);
          },
          rules: [validations.text()],
        },
        {
          key: 'linkType',
          label: t('activityPlace:linkType.label'),
          required: true,
          onlyEdit: true,
          colSpan: 1,
          widgetProps: {
            size: 'large',
          },
          render: renderActivityPlaceLinkTypeSelect
        },
        {
          key: 'address.abstract',
          colSpan: 3,
          label: t('address'),
          widget: 'textarea',
          initialValue: user?.address && user?.address.abstract,
          widgetProps: {autoSize: {minRows: 2, maxRows: 5}, size: 'large'},
          required: true,
          extra: (
            <InfoBox
              title={'proposalRequest.form.general.address.help'}
              translate={'proposalRequest'}
            />
          ),
          className: 'form-view-mode-vertical-item',
        },
        {
          key: 'address.location',
          label: t('location'),
          colSpan: 3,
          onlyEdit: true,
          required: false,
          widgetProps: {
            isOnClickActive: true,
            province: location?.province,
            municipality: location?.municipality,
          },
          render: (props) => renderMapViewer(props, location?.province)
        }
      );
      
      if (linkType === ACTIVITY_PLACE_LINK_TYPES.OTHER)
        fieldsMap.splice(4, 0,
          {
            key: 'linkTypeDescription',
            colSpan: 3,
            label: t('common:description'),
            widget: 'textarea',
            initialValue: '',
            widgetProps: {autoSize: {minRows: 2, maxRows: 5}, size: 'large'},
            required: true,
            extra: <InfoBox title={'activityPlace:description'} translate={'activityPlace'}/>,
            className: 'form-view-mode-vertical-item',
          });
      
      if (!isLoading && data?.total)
        fieldsMap.splice(6, 0, {
          key: 'onProtectedArea',
          colSpan: 3,
          dependencies: [['address', 'province']],
          label: t('proposalRequest.form.general.onProtectedArea.label'),
          render: renderQuestionWithDetails,
          extra: <InfoBox title={'proposalRequest.form.general.onProtectedArea.help'} translate={'proposalRequest'}/>,
          widgetProps: {
            withFile: true,
            extraLabel: t('proposalRequest.form.general.onProtectedArea.name'),
            fileLabel: 'proposalRequest.form.general.onProtectedArea.fileLabel',
            fileUploadLabel: 'proposalRequest.form.general.onProtectedArea.uploadLabel',
            placeholderText: 'selectPlaceholder',
            translate: 'proposalRequest',
            size: 'large',
            filters: new OperatorFilter({
              type: 'AND', filters: [
                new TermFilter({field: 'address.province', value: location?.province}),
                new TermFilter({field: 'municipalities', value: location?.municipality})
              ]
            }).toQuery(),
            customWhereCmp: ProtectedAreaSelect
          },
        }
        );
    }
    return fieldsMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    isViewMode,
    t,
    user?.address,
    user?.idNumber,
    data?.total,
    location,
    activities,
    mainActivity,
    settings,
  ]);
};

export const useProcedurePartner = (isViewMode, form, columns, initialValues) => {
  const {setProposal} = useProposalCreation();
  const {t} = useTranslation('proposalRequest');
  const {partners, statePartners} = form?.getFieldsValue();
  const isStateMipyme = [STATE_MIPYME, ORGANIZATION_PMS].includes(initialValues?.subjectType);
  const {totalAmount} = usePartnersCapital((isStateMipyme ? statePartners : partners) || []);
  
  return useMemo(() => {
    const fieldsMap = [];
    const isCNA = CNA === initialValues?.subjectType;
    
    if (!isViewMode) {
      if (!isStateMipyme) {
        fieldsMap.splice(
          1,
          0,
          {
            key: 'partners',
            colSpan: 3,
            render: createFormFieldset({
              title: 'proposalDetails.partnersAndWorkers.partners.title',
              translation: 'proposalRequest',
            }),
          },
          {
            key: 'showSocialCapitalAmount',
            colSpan: 3,
            label: t('proposalRequest.form.partners.socialCapital.title'),
            widget: ProposalCapitalSummary,
            dependencies: ['partners'],
            widgetProps: {
              partners,
              isCNA,
              isProcedure: true
            },
            rules: [
              {
                validator: () => {
                  return new Promise((resolve, reject) => {
                    if (!totalAmount) {
                      if (isCNA)
                        reject(
                          new Error(
                            i18n.t(
                              'proposalRequest:proposalRequest.form.partners.socialCapital.required',
                              {
                                label: i18n.t(
                                  `proposalRequest:proposalRequest.form.partners.socialCapital.${
                                    isCNA ? 'initialAmountOfWork' : 'monetaryAmountSummary'
                                  }`
                                ),
                              }
                            )
                          )
                        );
                    } else {
                      resolve();
                    }
                  });
                },
              },
            ],
          },
          {
            key: 'partners',
            colSpan: 3,
            required: true,
            widget: ProposalPrivatePartners,
            widgetProps: {
              proposal: initialValues,
              setProposal,
              allowAddPartners: true,
              notIncludeUser: true,
              allowFullChanges: true,
              isProcedure: true
            },
            initialValue: [],
            rules: [
              {
                validator: (rule, value) => {
                  // Do async validation to check if username already exists
                  // Use setTimeout to emulate api call
                  return new Promise((resolve, reject) => {
                    const someBlank = value?.some(
                      (e) => !e?.contribution && (!e?.goods || !e?.goods?.length)
                    );
                    const someBlankBaseImport = value?.some((e) => !e?.importBase && !e.isRetired);
                    if (isCNA) {
                      resolve();
                    } else if (someBlank) {
                      reject(
                        new Error(
                          i18n.t(
                            'proposalRequest:proposalRequest.form.partners.contributionIsRequired'
                          )
                        )
                      );
                    } else if (someBlankBaseImport) {
                      reject(
                        new Error(
                          i18n.t('proposalRequest:proposalRequest.form.partners.importBaseRequired')
                        )
                      );
                    } else {
                      resolve();
                    }
                  });
                },
              },
            ],
          }
        );
      } else {
        fieldsMap.splice(1, 0,
          {
            key: 'showSocialCapitalAmount2',
            colSpan: 3,
            label: t('proposalRequest.form.partners.socialCapital.title'),
            widget: ProposalCapitalSummary,
            dependencies: ['statePartners'],
            widgetProps: {
              partners: statePartners,
              isCNA,
              proposal: initialValues,
            },
            rules: [
              {
                validator: () => {
                  return new Promise((resolve, reject) => {
                    if (!totalAmount) {
                      reject(
                        new Error(
                          i18n.t(
                            'proposalRequest:proposalRequest.form.partners.socialCapital.required',
                            {
                              label: i18n.t(
                                'proposalRequest:proposalRequest.form.partners.socialCapital.monetaryAmountSummary'
                              ),
                            }
                          )
                        )
                      );
                    } else {
                      resolve();
                    }
                  });
                },
              },
            ],
          },
          {
            key: 'partners',
            colSpan: 3,
            render: createFormFieldset({
              title: 'proposalDetails.partnersAndWorkers.partners.title',
              translation: 'proposalRequest',
            }),
          },
          {
            key: 'statePartners',
            colSpan: 3,
            required: true,
            widget: ProposalStatePartners,
            initialValue: [],
            widgetProps: {
              proposal: initialValues,
              setProposal
            },
            rules: [
              {
                validator: (rule, value) => {
                  return new Promise((resolve, reject) => {
                    const someBlank = value?.some(e => (!e?.contribution && (!e?.goods || !e?.goods?.length)));
                    const withOutRepresentative = value.some(e => !e?.representative);
                    if (value && (withOutRepresentative || someBlank)) {
                      if (someBlank)
                        reject(new Error(i18n.t('proposalRequest:proposalRequest.form.partners.contributionIsRequired')));
                      else
                        reject();
                    } else {
                      resolve();
                    }
                  });
                }
              },
            ]
          },
          {
            key: 'stateBoss',
            colSpan: 3,
            // eslint-disable-next-line react/display-name
            render: () => <FormTitleSession translate={'proposalRequest'}
              title={'proposalRequest.form.partners.statePartnerBoss.title'}/>
          },
          {
            key: 'statePartnerBoss.firstName',
            colSpan: 1.5,
            label: t('firstName'),
            onlyEdit: true,
            required: true,
            extra: <InfoBox title={'proposalRequest.form.partners.statePartnerBoss.firstNameHelp'}
              translate={'proposalRequest'}/>,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.middleName',
            colSpan: 1.5,
            label: t('middleName'),
            onlyEdit: true,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.lastName',
            colSpan: 1.5,
            label: t('lastName'),
            onlyEdit: true,
            required: true,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.secondName',
            colSpan: 1.5,
            label: t('secondName'),
            onlyEdit: true,
            required: false,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.idNumber',
            colSpan: 1.5,
            widgetProps: {
              size: 'large',
            },
            label: t('proposalRequest.form.partners.statePartnerBoss.dni.placeholder'),
            required: true,
            rules: [CubaCIRuler],
          },
          {
            key: 'statePartnerBoss.email',
            widgetProps: {
              size: 'large',
            },
            colSpan: 1.5,
            label: t('email'),
            renderView: createClearRenderView(),
            rules: [{required: true}, {type: 'email'}],
          },
          {
            key: 'statePartnerBoss.phone',
            colSpan: 3,
            required: true,
            label: t('phones'),
            widgetProps: {
              size: 'large',
            },
            rules: [validations.listPhones(), validations.phoneValidator()],
            widget: PhoneEditor
          });
      }
    }
    return fieldsMap;
  }, [isStateMipyme, isViewMode, partners, setProposal, statePartners, t, totalAmount, initialValues]);
};

export const useProcedureAdministrationControlBody = (isViewMode, form, columns, initialValues, process) => {
  const {company, procedure} = useProcedure();
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      fieldsMap.splice(
        0, 0,
        {
          key: 'organization',
          colSpan: 3,
          render: createFormFieldset({
            title: 'steps.organizations',
            translation: 'proposalRequest',
          }),
        },
        {
          key: 'groupsHelp',
          colSpan: 3,
          // eslint-disable-next-line react/display-name
          render: () => <InfoBox
            translate={'proposalRequest'}
            title={'proposalRequest.form.organizations.help'}
          />
        },
        {
          key: 'administration',
          colSpan: 3,
          // eslint-disable-next-line react/display-name
          render: () => <FormTitleSession translate={'proposalRequest'} title={'steps.administration'}/>
        },
        {
          key: 'administrationControl',
          colSpan: 3,
          widget: ProposalPartnersAdministration,
          widgetProps: {
            canAddRepresentative: true,
            addRepresentativeText: 'proposalRequest.form.organizations.administration.addRepresentativeText',
            dialogContext: DIALOG_ADMIN_GENERAL_PARTNER_NAMESPACE,
            externalData: {...company, ...procedure?.dataByProcess?.[PROCEDURE_TYPES.CHANGE_PARTNER], ...procedure?.dataByProcess?.[process]}
          },
          rules: [
            {
              validator: (rule, value) => {
                return new Promise((resolve, reject) => {
                  const elements = value?.members?.filter(e => !!e);
                  const w = elements?.map(e => e?.idNumber);
                  const dict = new Set(w);
                  if (!elements || !elements?.length)
                    return resolve();
                  if (_uniq(w).length !== w.length) {
                    reject(new Error(i18n.t('proposalRequest:proposalRequest.form.organizations.inspectionControl.verificationMembers')));
                  } else {
                    const exist = dict.has(value.representative?.idNumber);
                    if (exist || dict.size !== elements?.length) {
                      reject(new Error(i18n.t('proposalRequest:proposalRequest.form.organizations.inspectionControl.verification')));
                    } else {
                      resolve();
                    }
                  }
                });
              }
            },
          ]
        },
        {
          key: 'control',
          colSpan: 3,
          // eslint-disable-next-line react/display-name
          render: () => <FormTitleSession translate={'proposalRequest'} title={'steps.control'}/>
        },
        {
          colSpan: 3,
          key: 'inspectionControl',
          widget: ProposalPartnersAdministration,
          widgetProps: {
            canAddRepresentative: true,
            addRepresentativeText: 'proposalRequest.form.organizations.administration.addRepresentativeText',
            dialogContext: DIALOG_CONTROL_GENERAL_PARTNER_NAMESPACE,
            externalData: {...company, ...procedure?.dataByProcess?.[PROCEDURE_TYPES.CHANGE_PARTNER], ...procedure?.dataByProcess?.[process]}
          },
          rules: [
            {
              validator: (rule, value) => {
                return new Promise((resolve, reject) => {
                  const elements = value?.members?.filter(e => !!e);
                  const w = elements?.map(e => e?.idNumber);
                  const dict = new Set(w);
                  if (!elements || !elements?.length)
                    return resolve();
                  if (_uniq(w).length !== w.length) {
                    reject(new Error(i18n.t('proposalRequest:proposalRequest.form.organizations.inspectionControl.verificationMembers')));
                  } else {
                    const exist = dict.has(value.representative?.idNumber);
                    if (exist || dict.size !== elements?.length) {
                      reject(new Error(i18n.t('proposalRequest:proposalRequest.form.organizations.inspectionControl.verification')));
                    } else {
                      resolve();
                    }
                  }
                });
              }
            },
          ]
        },
      );
    }
    return fieldsMap;
  }, [company, isViewMode, procedure?.dataByProcess, process]);
};

export const useNotarialProcedure = (isViewMode, form) => {
  const {user} = useUser();
  const {t} = useTranslation('proposalRequest');
  const {activities, mainActivity, address: location, subjectType} = form?.getFieldsValue();
  const {data} = useCountProtectedAreasByProvince(location);
  const {data: settings} = useSettings();
  
  const type = useMemo(() => subjectType || user?.subjectType, [subjectType, user?.subjectType]);
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      const activityDeclarationIndex = fieldsMap.findIndex((e) => e.key === 'activityDeclaration');
      
      fieldsMap.splice(activityDeclarationIndex + 1, 0,
        {
          key: 'notarialProcess',
          colSpan: 3,
          render: createFormFieldset({
            title: 'notarialProcess.stepTitle',
            translation: 'proposalRequest',
            help: t('notarialProcess.help')
          }),
        },
        {
          key: 'type',
          label: t('company:procedure.notaryProcedure.title'),
          colSpan: 3,
          widgetProps: {size: 'large'},
          required: true,
          widget: NotaryProcedureTypesSelect,
        },
        {
          key: 'description',
          label: t('notarialProcess.description'),
          widget: Editor,
          colSpan: 3,
          widgetProps: {size: 'large'},
          required: true,
          className: 'form-view-mode-vertical-item'
        },
        {
          key: 'file',
          colSpan: 3,
          label: t('notarialProcess.file'),
          widget: UploadFileDragger,
          required: true,
          widgetProps: {
            isImage: false,
            listType: 'picture',
            cropImage: false,
            translation: 'company',
            descriptionText: 'uploadFile',
            maxCount: 10,
            multiple: true
          },
        }
      );
    }
    return fieldsMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, isViewMode, t, user?.address, user?.idNumber, data?.total, location, activities, mainActivity, settings]);
};

export const useProcedureCompanyDissolution = (isViewMode, form, columns, initialValues) => {
  const {t} = useTranslation('company');
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      if (initialValues?.status === COMPANY_STATUS.ON_DISSOLVE_PROCESS)
        return [
          {
            key: 'notaryProcess.file',
            colSpan: 3,
            label: t('procedure.companyDissolution.balance.label'),
            widget: UploadFileDragger,
            required: true,
            help: <InfoBox title={'procedure.companyDissolution.balance.help'} translate={'company'}
              action={<ModelProposalDownload
                identifier={MODELS_FILE_KEYS.COMPANY_LIQUIDATION_BALANCE}/>}/>,
            widgetProps: {
              isImage: false,
              listType: 'picture',
              cropImage: false,
              translation: 'company',
              descriptionText: 'upload',
              maxCount: 10,
              multiple: true
            },
          }
        ];
      return [
        {
          key: 'warning',
          colSpan: 3,
          // eslint-disable-next-line react/display-name
          render: () => <DissolutionWarning/>
        },
        {
          key: 'hasOperated',
          colSpan: 3,
          placeholder: 'defaultPlaceholder',
          required: false,
          widget: ProposalTrademark,
          widgetProps: {
            label: 'procedure.companyDissolution.hasOperated.label',
            descriptionText: 'procedure.companyDissolution.hasOperated.upload',
            helpText: 'procedure.companyDissolution.hasOperated.help',
            size: 'large',
            translate: 'company',
            type: 'text',
            maxCount: 10,
            multiple: true
          },
          rules: [
            {
              validator: (rule, value) => {
                return new Promise((resolve, reject) => {
                  if (value?.active && !value?.file)
                    reject(new Error(i18n.t('validateMessages.required')));
                  else {
                    resolve();
                  }
                });
              },
            },
          ],
        }];
    }
    return fieldsMap;
  }, [initialValues?.status, isViewMode, t]);
};

export const useProcedureChangeEstablishments = (isViewMode, form, columns, initialValues) => {
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      return [
        {
          key: 'activityPlace',
          colSpan: 3,
          widget: ActivityPlacesSection,
          widgetProps: {
            softDelete: true,
            procedure: initialValues?.procedure,
            isProcedureView: true,
            proposal: {
              _id: initialValues?.proposal,
              mainActivity: initialValues?.mainActivity,
              activities: initialValues?.activities
            }
          },
        }];
    }
    return fieldsMap;
  }, [initialValues, isViewMode]);
};

export const useMapColumns = (columns) => {
  const {processKeys} = useProcessSettings();
  const {t} = useTranslation('proposalFile');
  const {user, hasPermission} = useUser();
  
  const basicFields = useMemo(() => [
    'notaryOffice'
  ], []);
  
  const notaryColumnsFields = useMemo(() => [
    {
      key: `processDataByActors.${processKeys['EN']}.startDate`,
      title: 'procedure.columns.notaryStartDate',
      type: 'date',
      align: 'center',
      sorteable: true,
      ellipsis: true,
      dataIndex: ['processDataByActors', processKeys['EN'], 'startDate'],
      width: 150,
      responsive: ['lg'],
      //eslint-disable-next-line
      render: (value) => <DatePreview value={value} format={'LLL a'}/>
    },
    {
      key: 'notaryAppointmentDate',
      title: 'procedure.columns.notaryAppointmentDate',
      type: 'date',
      align: 'center',
      ellipsis: true,
      dataIndex: ['processDataByActors', processKeys['EN'], 'appointmentDate'],
      width: 150,
      responsive: ['lg'],
      //eslint-disable-next-line
      render: (value) => <BooleanPreview value={value?.length}/>
    },
  ], [processKeys]);
  
  return useMemo(() => {
    switch (user?.userType) {
      case USER_TYPES.NOTARY_DPJ: {
        const copy = columns.filter((e) => ![...basicFields, 'province'].includes(e?.key));
        return [
          ...take(copy, copy?.length - 1),
          ...notaryColumnsFields,
          {
            key: 'notaryOffice',
            sorteable: true,
            title: t('expedient.columns.notaryOffice'),
            dataIndex: 'notaryOffice',
            align: 'center',
            render: (_, entity) => renderAssignNotaryOffice(_, entity, ProcedureApiService, CACHE_KEY_LIST_PROCEDURE),
            width: 255,
          },
          last(copy),
        ];
      }
      case USER_TYPES.COMMERCIAL_REGISTER: {
        const copy = columns.filter((e) => ![...basicFields, 'province'].includes(e?.key));
        return [
          ...take(copy, copy?.length - 1),
          {
            key: `processDataByActors.${processKeys['IRM']}.startDate`,
            title: 'procedure.columns.irm',
            type: 'date',
            align: 'center',
            sorteable: true,
            dataIndex: ['processDataByActors', processKeys['IRM'], 'startDate'],
            width: 150,
            responsive: ['lg'],
            //eslint-disable-next-line
            render: (value) => <DatePreview value={value} format={'LLL a'}/>
          },
          {
            key: 'appointmentDate',
            title: 'procedure.columns.appointmentDate',
            type: 'date',
            align: 'center',
            dataIndex: ['processDataByActors', processKeys['IRM'], 'appointmentDate'],
            width: 150,
            responsive: ['lg'],
            //eslint-disable-next-line
            render: (value) => <BooleanPreview value={value?.length}/>
          },
          last(copy),
        ];
      }
      case USER_TYPES.NOTARY: {
        const copy = columns.filter((e) => ![...basicFields, 'province', 'municipality'].includes(e?.key));
        return [
          ...take(copy, copy?.length - 1),
          ...notaryColumnsFields,
          last(copy),
        ];
      }
      default:
        if (hasPermission('ADMIN')) {
          const copy = columns.filter((e) => !['notaryOffice'].includes(e?.key));
          return [
            ...take(copy, copy?.length - 1),
            {
              key: 'notaryOffice',
              sorteable: true,
              title: t('expedient.columns.notaryOffice'),
              dataIndex: 'notaryOffice',
              align: 'center',
              render: (_, entity) => renderAssignNotaryOffice(_, entity, ProcedureApiService, CACHE_KEY_LIST_PROCEDURE),
              width: 255,
            },
            last(copy),
          ];
        }
        return columns;
    }
  }, [basicFields, columns, hasPermission, notaryColumnsFields, processKeys, t, user?.userType]);
};

export const useProcedureTCPCancellation = (isViewMode, form, columns, initialValues, process, causeFilterType = CAUSE_TYPES.TCP_CANCELLATION_PROCEDURE, excludeActivities = []) => {
  const {t} = useTranslation('proposalRequest');
  const {user} = useUser();
  const isUser = user?.userType === USER_TYPES?.USER;
  
  return useMemo(() => {
    const fieldsMap = [];
    
    const activities = initialValues?._activities?.map(e => e?._id || e) || [];
    const mainActivity = initialValues?.mainActivity?._id;
    
    if (!isViewMode) {
      return [
        {
          key: 'cause',
          colSpan: 3,
          label: t('company:procedure.cause'),
          placeholder: t('selectPlaceholder'),
          widget: CauseSelect,
          required: true,
          widgetProps: {
            size: 'large',
            filters: isUser ?
              new OperatorFilter({
                type: 'AND',
                filters: [
                  new TermFilter({field: '_id', value: CAUSES_IDENTIFIERS.USER_CANCELLATION_CAUSE, objectId: true}),
                  new TermFilter({field: 'type', value: causeFilterType})
                ]
              }) :
              new TermFilter({field: 'type', value: causeFilterType}),
            softDelete: true,
            isProcedureView: true
          },
        },
        {
          key: 'activities',
          colSpan: 3,
          label: t('proposalRequest.form.general.activities.label'),
          placeholder: t('selectPlaceholder'),
          required: true,
          dependencies: ['mainActivity'],
          widget: ProposalActivitiesSelect,
          extra: (
            <InfoBox
              title={'proposalRequest.form.general.activities.help3'}
              translate={'proposalRequest'}
            />
          ),
          widgetProps: {
            filters: new InFilter({
              field: '_id',
              value: activities.concat([mainActivity]).filter(e => !excludeActivities?.includes(e))
            }),
            size: 'large',
          },
        },
        {
          key: 'description',
          colSpan: 3,
          label: t('description'),
          widget: 'textarea',
          widgetProps: {autoSize: {minRows: 2, maxRows: 5}, size: 'large'},
          className: 'form-view-mode-vertical-item',
        },
      ];
    }
    return fieldsMap;
  }, [isUser, causeFilterType, excludeActivities, initialValues?._activities, initialValues?.mainActivity?._id, isViewMode, t]);
};

export const useProcedureTCPSuspensionOfActivities = (isViewMode, form, columns, initialValues, process) => {
  const {t} = useTranslation('company');
  const fieldsMap = useProcedureTCPCancellation(
    isViewMode,
    form,
    columns,
    initialValues,
    process,
    CAUSE_TYPES.TCP_SUSPENSION_OF_ACTIVITIES,
    initialValues?.nonActiveActivities?.reduce((r, e) => {
      r = r.concat(e?.activities?.map(a => a?._id || a));
      return r;
    }, [])
  );
  return useMemo(() => {
    
    if (!isViewMode) {
      fieldsMap.splice(
        2,
        0,
        {
          key: 'untilDate',
          widget: DynamicFieldDatePicker,
          colSpan: 3,
          required: true,
          onlyEdit: true,
          widgetProps: {
            size: 'large',
            className: 'w-full',
            disabledDate: (current) => current &&
              (current < moment().endOf('day') ||
                current > moment().add(6, 'month').startOf('day')
              ),
          },
          label: t('procedureDetail.tcpSuspensionOfActivities.untilDate'),
          extra: (
            <InfoBox
              title={'procedureDetail.tcpSuspensionOfActivities.help'}
              translate={'company'}
            />
          ),
        });
    }
    return fieldsMap;
  }, [fieldsMap, isViewMode, t]);
};

export const useProcedureTCPCorporatePurpose = (isViewMode, form) => {
  const {t} = useTranslation('proposalRequest');
  const {activities, mainActivity} = form?.getFieldsValue();
  
  useEffect(() => {
    const exist = activities?.some(e => (e?.id || e) === mainActivity);
    if (exist) {
      const values = form.getFieldsValue(true);
      set(values, 'activities', activities?.filter(e => (e?.id || e) !== mainActivity));
      form.setFieldsValue(cloneDeep(values));
    }
  }, [activities, form, mainActivity]);
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      return [
        {
          key: 'activityDeclaration',
          colSpan: 3,
          render: createFormFieldset({
            title: 'proposalRequest.form.general.groups.activities',
            help: 'proposalRequest.form.general.groups.activities_help',
            translation: 'proposalRequest',
          }),
        },
        {
          key: 'socialObject',
          label: t('proposalRequest.form.general.socialObject.label'),
          widget: Editor,
          colSpan: 3,
          widgetProps: {size: 'large'},
          required: true,
          className: 'form-view-mode-vertical-item',
        },
        {
          key: 'mainActivity',
          colSpan: 3,
          label: t('proposalRequest.form.general.mainActivity.label'),
          placeholder: t('selectPlaceholder'),
          required: true,
          dependencies: ['activities'],
          widget: CNAESelect,
          widgetProps: {
            size: 'large',
            filters: new TermFilter({field: 'isProhibited', value: false}).toQuery()
          },
        },
        {
          key: 'otherActivities',
          label: t('proposalRequest.form.general.activities.textLabel'),
          colSpan: 3,
          widget: Editor,
          widgetProps: {size: 'large'},
          className: 'form-view-mode-vertical-item',
        },
        {
          key: 'activities',
          colSpan: 3,
          label: t('proposalRequest.form.general.activities.label'),
          placeholder: t('selectPlaceholder'),
          required: false,
          dependencies: ['mainActivity'],
          widget: ProposalActivitiesSelect,
          extra: (
            <InfoBox
              title={'proposalRequest.form.general.activities.help'}
              translate={'proposalRequest'}
            />
          ),
          widgetProps: {
            filters: new TermFilter({field: 'isProhibited', value: false}).toQuery(),
            mainActivity: mainActivity,
            size: 'large',
          },
        }
      ];
    }
    return fieldsMap;
  }, [isViewMode, mainActivity, t]);
};

export const useProcedureTCPUpdateEstablishments = (isViewMode, form, columns, initialValues) => {
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      return [
        {
          key: 'activityPlace',
          colSpan: 3,
          widget: ActivityPlacesSection,
          widgetProps: {
            title: 'proposalRequest.form.activityPlace.titleTCP',
            helpText: 'proposalRequest.form.activityPlace.helpTCP',
            softDelete: true,
            procedure: initialValues?.procedure,
            isProcedureView: true,
            isTCPProposal: true,
            proposal: {
              _id: initialValues?.proposal,
              mainActivity: initialValues?.mainActivity,
              activities: initialValues?.activities
            }
          },
        }
      ];
    }
    return fieldsMap;
  }, [initialValues?.activities, initialValues?.mainActivity, initialValues?.procedure, initialValues?.proposal, isViewMode]);
};

export const useProcedureTCPUpdateRepresentatives = (isViewMode) => {
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      return [
        {
          key: 'representatives',
          colSpan: 3,
          render: createFormFieldset({
            title: 'procedure.tcpUpdateInformation.representatives',
            translation: 'company',
          }),
        },
        {
          key: 'permissions_representation_lease',
          widget: QuestionWithDetails,
          colSpan: 3,
          widgetProps: {
            label: 'company:procedure.tcpUpdateInformation.houselease',
            translate: 'proposalRequest',
            extraLabel: 'proposalRequest.permissions.representation',
            size: 'large',
            options: representationFields,
          }
        },
        {
          key: 'permissions_representation_transport',
          widget: QuestionWithDetails,
          colSpan: 3,
          widgetProps: {
            label: 'company:procedure.tcpUpdateInformation.transportlease',
            translate: 'proposalRequest',
            extraLabel: 'proposalRequest.permissions.representation',
            size: 'large',
            options: representationFields,
          }
        },
      ];
    }
    return fieldsMap;
  }, [isViewMode]);
};

export const useProcedureTCPUpdateGeneralInfo = (isViewMode, form, columns, initialValues) => {
  const {t} = useTranslation('proposalRequest');
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      return [
        {
          key: 'miPymeExpectedWorkers',
          colSpan: 1,
          label: t('proposalRequest.form.partners.workersProjection.labelTCP'),
          widgetProps: {
            max: 3, min: 0, type: 'text', size: 'large'
          },
          widget: 'number',
          placeholder: t('amountPlaceHolder'),
          required: true
        },
        {
          key: 'permissions_posters',
          widget: QuestionWithDetails,
          colSpan: 3,
          initialValue: initialValues?.permissions_posters,
          widgetProps: {
            label: 'company:procedure.tcpUpdateInformation.posters.label',
            translate: 'proposalRequest',
            extraLabel: 'proposalRequest.permissions.posters',
            size: 'large',
            options: posterFields,
          },
          help: <InfoBox title={'procedure.tcpUpdateInformation.posters.help'} translate={'company'}/>,
        },
      ];
    }
    return fieldsMap;
  }, [initialValues?.permissions_posters, isViewMode, t]);
};

export const useProcedureTCPSocialContribution = (isViewMode, form, columns, initialValues) => {
  const {t} = useTranslation('proposalRequest');
  
  const partner = initialValues?.partners?.[0] || null;
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      return [
        {
          key: 'importBase',
          label: t('proposalRequest.form.partners.importBase.title'),
          required: true,
          colSpan: 3,
          placeholder: 'defaultPlaceholder',
          widget: ProposalPartnerImportSelect,
          initialValue: partner?.contribution && partner?.contribution,
          extra: (
            <InfoBox
              title={'proposalRequest.form.partners.importBase.help'}
              translate={'proposalRequest'}
            />
          ),
        }
      ];
    }
    return fieldsMap;
  }, [isViewMode, partner?.contribution, t]);
};

export const useProcedureTCPUpdateRegisteredAddress = (isViewMode, form, columns, initialValues) => {
  const {t} = useTranslation('proposalRequest');
  const {address: location} = form?.getFieldsValue();
  
  const partner = initialValues?.partners?.[0] || null;
  
  return useMemo(() => {
    const fieldsMap = [];
    
    if (!isViewMode) {
      return [
        {
          key: 'socialAddress',
          colSpan: 3,
          render: createFormFieldset({
            title: 'proposalRequest.form.general.socialAddress.label',
            help: 'proposalRequest.form.general.socialAddress.help',
            translation: 'proposalRequest',
          }),
        },
        {
          key: 'address.province',
          required: true,
          colSpan: 1.5,
          label: t('province'),
          widget: ProvinceSelect,
          initialValue: partner?.address && partner?.address.province,
          widgetProps: {
            size: 'large',
            onChange: () => form.setFieldsValue({bankType: '', bankOffice: ''}),
          },
          viewWidget: ({value}) => renderProvince(value),
        },
        {
          key: 'address.municipality',
          label: t('municipality'),
          required: true,
          onlyEdit: true,
          initialValue: partner?.address && partner?.address.municipality,
          colSpan: 1.5,
          widgetProps: {
            size: 'large',
          },
          render: ({...rest}) => {
            return renderMunicipalitySelect(rest, location?.province);
          },
          rules: [validations.text()],
        },
        {
          key: 'address.abstract',
          colSpan: 3,
          label: t('address'),
          widget: 'textarea',
          initialValue: partner?.address && partner?.address.abstract,
          widgetProps: {autoSize: {minRows: 2, maxRows: 5}, size: 'large'},
          required: true,
          extra: (
            <InfoBox
              title={'proposalRequest.form.general.address.help'}
              translate={'proposalRequest'}
            />
          ),
          className: 'form-view-mode-vertical-item',
        },
        {
          key: 'address.location',
          label: t('location'),
          colSpan: 3,
          onlyEdit: true,
          required: false,
          widgetProps: {
            isOnClickActive: true,
            province: location?.province,
            municipality: location?.municipality,
          },
          render: (props) => renderMapViewer(props, location?.province)
        }
      ];
    }
    return fieldsMap;
  }, [form, isViewMode, location?.municipality, location?.province, partner?.address, t]);
};
